import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FormInput } from '@podus/ui';
import { MdClose } from 'react-icons/md';
import { api } from '@/lib/api';
import { SuccessPanel } from './shared';

/* ─── Reject Case Modal ─── */

interface RejectCaseModalProps {
    open: boolean;
    caseId: number;
    onClose: () => void;
    onRejected?: () => void;
}

export function RejectCaseModal({ open, caseId, onClose, onRejected }: RejectCaseModalProps) {
    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!open) {
            setDone(false);
            return;
        }
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    const handleClose = () => {
        setReason('');
        setDescription('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!reason.trim()) return;
        setIsLoading(true);
        try {
            await api.post(`/api/cases/${caseId}/reject`, { reasonCategory: reason, description });
            setDone(true);
            onRejected?.();
        } catch {
            // keep modal open
        } finally {
            setIsLoading(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <>
            <div className="fixed inset-0 z-60 bg-black/40" onClick={handleClose} />
            <div className="fixed inset-0 z-61 flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                    {done ? (
                        <SuccessPanel
                            title="Case Rejected"
                            message="The case has been rejected and the admin team has been notified."
                            gradientId="reject-grad"
                            onClose={handleClose}
                        />
                    ) : (
                        <>
                            <div className="flex items-center justify-between px-6 pt-6 pb-4">
                                <h2 className="text-lg font-bold text-secondary">Case Rejection</h2>
                                <button
                                    type="button"
                                    title="Close"
                                    onClick={handleClose}
                                    className="flex h-8 w-8 items-center justify-center rounded-full text-[#555] transition-colors hover:bg-[#f5f5f5]"
                                >
                                    <MdClose size={20} />
                                </button>
                            </div>

                            <div className="px-6">
                                <FormInput
                                    id="reject-reason"
                                    placeholder="Reason Category"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    variant="white"
                                />
                            </div>

                            <div className="mt-4 px-6">
                                <label
                                    htmlFor="reject-desc"
                                    className="mb-1 block text-[13px] font-semibold text-secondary"
                                >
                                    Description
                                </label>
                                <textarea
                                    id="reject-desc"
                                    rows={6}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full rounded-lg border border-[#D2D2D2] bg-white px-4 py-3 text-[13px] text-secondary outline-none transition-colors placeholder:text-[#52525B] focus:border-secondary focus:ring-2 focus:ring-secondary"
                                />
                            </div>

                            <div className="px-6 pb-6 pt-4">
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={!reason.trim() || isLoading}
                                    className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[13px] font-bold text-white transition-all hover:bg-secondary-400 disabled:opacity-60"
                                >
                                    {isLoading ? 'Submitting…' : 'Submit Reason'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>,
        document.body,
    );
}
